/**
 * Integration helpers — shared across the app for any UI surface that displays
 * which integration(s) a credential lives in.
 *
 * Used by:
 *   - CredentialsTab (table column)
 *   - CredentialDrawer (detail view)
 *   - Future: bulk preview, audit logs, reports
 */

import React from 'react';
import { Flex, Tag, Typography } from 'zui/Antd';
import { INTEGRATIONS_TYPES } from 'config/integrationTypes';

const { Text } = Typography;

/**
 * Tag visual config per integration.
 * Colors are chosen to be visually distinct from CREDENTIAL_TYPE_CONFIG (blue/green)
 * and from typical status tags (red/green).
 */
export const INTEGRATION_TAG_CONFIG = {
	[INTEGRATIONS_TYPES.INTEGRATION_INNER_RANGE.id]: { label: 'Inner Range', color: 'geekblue' },
	[INTEGRATIONS_TYPES.INTEGRATION_CONTROL.id]: { label: 'Control', color: 'purple' },
	[INTEGRATIONS_TYPES.INTEGRATION_CONTROL_GATEWAY.id]: { label: 'Control Gateway', color: 'orange' },
	[INTEGRATIONS_TYPES.INTEGRATION_TTLOCK.id]: { label: 'TTLock', color: 'magenta' }
};

/**
 * Extracts the unique set of integrationIDs a credential record covers.
 *
 * Handles both shapes:
 *   - Grouped record (from groupCredentialsByPhysicalIdentifier):
 *       record.integrations = [{ integrationID, credentialID }, ...]
 *   - Raw record (single row from selectCredentialByID):
 *       record.integrationID = <number>
 *
 * @param {Object} credential
 * @returns {number[]} Unique integration IDs (e.g. [1, 2] for dual-tech)
 */
export const getIntegrationIDsFromCredential = (credential) => {
	if (!credential) return [];
	if (credential.integrations?.length) {
		return [...new Set(credential.integrations.map((i) => i.integrationID).filter(Boolean))];
	}
	return credential.integrationID ? [credential.integrationID] : [];
};

/**
 * Renders integration tags for a credential. Used by both the table and drawer
 * to keep the visual treatment identical. Returns "—" placeholder when the
 * credential has no integration association (shouldn't normally happen).
 *
 * @param {Object} credential
 * @param {Object} [options]
 * @param {string} [options.gap='4'] — Flex gap between tags
 * @param {Object} [options.placeholderStyle] — Style for the "—" fallback
 */
/**
 * Returns the integration config for a device based on its defaultCategoryID.
 * @param {number} defaultCategoryID
 * @returns {{ id: number, name: string, color: string } | null}
 */
export const getIntegrationFromDevice = (device) => {
	if (!device?.defaultCategoryID) return null;
	for (const integration of Object.values(INTEGRATIONS_TYPES)) {
		if (integration.defaultCategoryIDs?.includes(device.defaultCategoryID)) {
			const tagConfig = INTEGRATION_TAG_CONFIG[integration.id];
			return { id: integration.id, name: integration.name, color: tagConfig?.color || 'default' };
		}
	}
	return null;
};

/**
 * Builds Select options for devices with integration tags.
 * Used across all permission modals for consistent device display.
 *
 * @param {Array} devices - Array of device objects with { deviceID, name, defaultCategoryID }
 * @returns {Array} Options with { label: ReactNode, value: number, searchLabel: string }
 */
export const buildDeviceOptionsWithIntegration = (devices) => {
	return devices.map((d) => {
		const integration = getIntegrationFromDevice(d);
		return {
			label: (
				<Flex align='center' justify='space-between' style={{ width: '100%' }}>
					<Text style={{ fontSize: 13 }}>{d.name}</Text>
					{integration && (
						<Tag color={integration.color} style={{ margin: 0, fontSize: 11 }}>
							{integration.name}
						</Tag>
					)}
				</Flex>
			),
			displayLabel: d.name,
			value: d.deviceID,
			searchLabel: `${d.name} ${integration?.name || ''}`
		};
	});
};

/**
 * Builds Select options for device groups with type tags.
 *
 * @param {Array} groups - Array of group objects with { groupID, name, typeID }
 * @returns {Array} Options with { label: ReactNode, value: number, displayLabel: string, searchLabel: string }
 */
export const buildGroupOptionsWithType = (groups) => {
	const GROUP_TAG_CONFIG = {
		2: { label: 'Control', color: 'purple' },
		3: { label: 'Inner Range', color: 'geekblue' }
	};

	return groups.map((g) => {
		const tagConfig = GROUP_TAG_CONFIG[g.typeID];
		return {
			label: (
				<Flex align='center' justify='space-between' style={{ width: '100%' }}>
					<Text style={{ fontSize: 13 }}>{g.name}</Text>
					{tagConfig && (
						<Tag color={tagConfig.color} style={{ margin: 0, fontSize: 11 }}>
							{tagConfig.label}
						</Tag>
					)}
				</Flex>
			),
			displayLabel: g.name,
			value: g.groupID,
			searchLabel: `${g.name} ${tagConfig?.label || ''}`
		};
	});
};

export const renderIntegrationTags = (credential, { gap = 4, placeholderStyle } = {}) => {
	const integrationIDs = getIntegrationIDsFromCredential(credential);

	if (integrationIDs.length === 0) {
		return (
			<Text type='secondary' style={{ fontSize: 12, ...placeholderStyle }}>
				—
			</Text>
		);
	}

	return (
		<Flex gap={gap} wrap='wrap'>
			{integrationIDs.map((id) => {
				const config = INTEGRATION_TAG_CONFIG[id];
				if (!config) return null;
				return (
					<Tag key={id} color={config.color} style={{ margin: 0 }}>
						{config.label}
					</Tag>
				);
			})}
		</Flex>
	);
};
